import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { catchError, forkJoin, map, mergeMap, Observable, of, tap, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { mapGraphQLGuardianToIGuardian } from '../../graphql/functions/mappingFunctions';
import { getSimpleGuardianInfoQuery, getUserAccountInfoQuery } from '../../graphql/functions/queryStrings';
import { Content, Data, IEmail, LinkedAccount } from '../../graphql/models/gqlModels';
import { ILinkedAccountInfo } from '../../models/access';
import { IGuardian, ILearnerAccess } from '../../models/user';
import { AccessActions } from '../../store/access/access.actions';
import { selectSelectedAccountCodes } from '../../store/access/access.selectors';
import { AppState } from '../../store/app.state';
import { selectLoggedInGuardianId } from '../../store/guardian/guardian.selectors';
import { LookupService } from '../lookup/lookup.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly lookupService = inject(LookupService);

  constructor(
    private readonly apollo: Apollo,
    private readonly http: HttpClient,
    private readonly store: Store<AppState>,
  ) {}

  linkedAccountIds = signal<ILinkedAccountInfo[]>([]);
  selectedAccountCodes = toSignal(this.store.select(selectSelectedAccountCodes || []));
  loggedInGuardianId = toSignal(this.store.select(selectLoggedInGuardianId), { initialValue: null });

  getLoggedInUserInfo(id: string): Observable<IGuardian | null> {
    if (!id) {
      console.error('getLoggedInUserInfo: No ID provided');
      return of(null);
    }

    const query = gql`
      ${getUserAccountInfoQuery(id)}
    `;

    return this.apollo
      .query<Data>({
        query,
        fetchPolicy: 'network-only',
      })
      .pipe(
        mergeMap((response) => {
          // Log errors but don't immediately return null if we have usable data
          if (response.errors && response.errors.length > 0) {
            console.warn('GraphQL errors (but checking for usable data):', response.errors);
          }

          // Only return null if we truly have no usable data
          if (!response.data?.guardianSearch?.content || response.data.guardianSearch.content.length === 0) {
            console.warn('Guardian search returned no content');
            return of(null);
          }
          const contents: Content[] = response.data.guardianSearch.content;
          if (contents.length === 0) {
            console.warn(`No guardian found with ID: ${id}`);
            return of(null);
          }
          try {
            const linkedAccounts: LinkedAccount[] = contents[0].linkedAccounts ?? [];
            if (linkedAccounts.length === 0) {
              this.linkedAccountIds.set([]);
              return of(mapGraphQLGuardianToIGuardian(contents[0], this.lookupService));
            }
            // Prepare all queries for linked accounts
            const queries = linkedAccounts.map((account) => {
              const linkedQuery = gql`
              query AccountSearch {
                accountSearch(
                  criteria: {accountCode: "${account.accountCode}", company: {companyCode: "${account.company.companyCode}"}}
                ) {
              content {
                allocation {
                  guardianLinks {
                    id
                    myhubInviteStatusCode
                    guardian {
                      id
                      person {
                        firstName
                        lastName
                        idNumber
                        emailAddresses {
                          id
                          address
                          emailAddressType {
                            id
                            description
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
              }`;
              return this.apollo
                .query<Data>({
                  query: linkedQuery,
                  fetchPolicy: 'network-only',
                })
                .pipe(
                  map((linkedResponse) => {
                    const guardianInfos: ILinkedAccountInfo[] = [];
                    const contents = linkedResponse.data?.accountSearch?.content || [];
                    contents.forEach((content) => {
                      (content.allocation || []).forEach((allocation) => {
                        (allocation.guardianLinks || []).forEach((link) => {
                          if (
                            link.guardian.person &&
                            link.guardian.person.idNumber &&
                            link.guardian.person.idNumber !== id
                          ) {
                            let guardianEmail: IEmail | undefined;
                            (link.guardian.person.emailAddresses ?? []).some((email) => {
                              if (email.id && email.address && email.emailAddressType?.id) {
                                guardianEmail = {
                                  id: email.id,
                                  address: email.address,
                                  emailAddressType: {
                                    id: email.emailAddressType.id,
                                    description: email.emailAddressType.description,
                                  },
                                };
                                return true;
                              }
                              return false;
                            });

                            guardianInfos.push({
                              id: link.id,
                              guardianId: link.guardian.id,
                              guardianIdNumber: link.guardian.person.idNumber,
                              guardianName: link.guardian.person.firstName,
                              gaurdianSurname: link.guardian.person.lastName,
                              guardianEmail,
                              accessChangeGuardianId: link.guardian.id, // Store the guardian for whom the access change occurred (the logged-in guardian)
                              myhubInviteStatusCode: link.myhubInviteStatusCode || 'IAC',
                              accountLinkId: link.id, // Store the guardianLinks ID, not allocation ID
                              accountCode: account.accountCode, // Store the account code used in this query
                              companyCode: account.company.companyCode, // Store the company code used in this query
                            });
                          }
                        });
                      });
                    });
                    // Return the unique guardian info objects
                    for (const guardianInfo of guardianInfos) {
                      const existingIndex = this.linkedAccountIds().findIndex(
                        (existing) => existing.guardianId === guardianInfo.guardianId,
                      );
                      if (existingIndex === -1) {
                        this.linkedAccountIds.update((ids) => [...ids, guardianInfo]);
                      }
                    }
                    return guardianInfos;
                  }),
                  catchError((error) => {
                    console.error(`Failed to fetch linked accounts for ${account.accountCode}:`, error);
                    return of([]);
                  }),
                );
            });
            // Wait for all queries to complete
            return forkJoin(queries).pipe(
              map((results) => {
                // Flatten and deduplicate guardian info objects
                const allInfos = results.flat();
                const uniqueInfos = allInfos.reduce((acc: ILinkedAccountInfo[], current) => {
                  const existingIndex = acc.findIndex(
                    (item) =>
                      item.guardianId === current.guardianId &&
                      item.accountCode === current.accountCode &&
                      item.companyCode === current.companyCode,
                  );
                  if (existingIndex === -1) {
                    acc.push(current);
                  }
                  return acc;
                }, []);
                this.linkedAccountIds.set(uniqueInfos);
                this.store.dispatch(AccessActions.loadGuardianIds({ guardianLinks: uniqueInfos }));
                this.store.dispatch(AccessActions.loadGuardianLinks({ guardianLinks: uniqueInfos }));
                return mapGraphQLGuardianToIGuardian(contents[0], this.lookupService);
              }),
            );
          } catch (error) {
            console.error(`Error mapping guardian with ID ${id}:`, error);
            console.debug('Contents that failed mapping:', JSON.stringify(contents[0]));
            return of(null);
          }
        }),
        catchError((error) => {
          console.error(`Failed to fetch guardian with ID ${id}:`, error);
          return of(null);
        }),
      );
  }

  getGuardians(): Observable<IGuardian[]> {
    //For each guardian, get the idNumber from linkedAccounts and then get the guardian info with a query
    let linkedInfos: ILinkedAccountInfo[] = this.linkedAccountIds();

    if (!linkedInfos || linkedInfos.length === 0) {
      return of([]);
    }

    //Deduplicate linkedInfos by guardianId to avoid multiple queries for the same guardian
    linkedInfos = linkedInfos.filter(
      (info, index, self) => index === self.findIndex((t) => t.guardianId === info.guardianId),
    );

    // Create an array of observables, each fetching one guardian's data with simplified query
    const guardianObservables = linkedInfos.map((info) => {
      const query = gql`
        ${getSimpleGuardianInfoQuery(info.guardianIdNumber)}
      `;
      return this.apollo
        .query<Data>({
          query,
          errorPolicy: 'all', // Allow partial data even with GraphQL errors
          fetchPolicy: 'network-only', // Ensure fresh data
        })
        .pipe(
          timeout(15000), // Increased timeout to 15 seconds per guardian
          map((response) => {
            // Log errors but don't immediately return null if we have usable data
            if (response.errors && response.errors.length > 0) {
              console.warn(
                `GraphQL errors for guardian ${info.guardianId} (but checking for usable data):`,
                response.errors,
              );
            }

            if (!response.data?.guardianSearch?.content?.[0]) {
              console.warn(`No guardian found with ID: ${info.guardianId}`);
              return null;
            }
            const guardian = mapGraphQLGuardianToIGuardian(response.data.guardianSearch.content[0], this.lookupService);
            // Map the invite status code to guardian status
            if (guardian) {
              guardian.status = this.mapInviteStatusToGuardianStatus(info.myhubInviteStatusCode);
              guardian.accountLinkId = info.accountLinkId; // Set the allocation ID

              // If the guardian status is 'IAC' (Inactive), add the account link
              if (info.myhubInviteStatusCode === 'IAC' && info.accountCode && info.companyCode) {
                if (!guardian.accountLinks) {
                  guardian.accountLinks = [];
                }
                // Check if this account link already exists to avoid duplicates
                const existingLink = guardian.accountLinks.find(
                  (link) => link.accountCode === info.accountCode && link.companyCode === info.companyCode,
                );
                if (!existingLink) {
                  guardian.accountLinks.push({
                    accountCode: info.accountCode,
                    companyCode: info.companyCode,
                  });
                }
              }
            }
            return guardian;
          }),
          catchError((error) => {
            if (error.name === 'TimeoutError') {
              console.warn(
                `Timeout occurred for guardian ${info.guardianId} after 15 seconds - skipping this guardian`,
              );
            } else {
              console.error(`Error fetching guardian with ID ${info.guardianId}:`, error);
            }
            return of(null); // Return null for failed requests so they don't block the entire process
          }),
        );
    });

    // Use forkJoin with proper error handling - each observable handles its own errors
    return forkJoin(guardianObservables).pipe(
      map((guardians) => guardians.filter((guardian) => guardian !== null) as IGuardian[]),
      catchError((error) => {
        // This should rarely be hit since individual observables handle their own errors
        console.error('Unexpected error in getGuardians forkJoin:', error);
        return of([]); // Return empty array if everything fails unexpectedly
      }),
    );
  }

  updateUserInfo(guardian: IGuardian): Observable<any> {
    const guardianMutation = gql`
      mutation UpdateGuardian {
        updateGuardian(
          input: {id: "${guardian.id}", parentalStatus: {id: "${guardian.parentalStatus.id}"}, relationship: {id: "${guardian.learnerRelationship?.id}"}, maritalStatus: "${guardian.maritalStatus}", employerName: "${guardian.employment?.employer}", occupation: "${guardian.employment?.occupation}"}
        ) {
          parentalStatus {
            id
          }
          relationship {
            id
          }
          maritalStatus
          employerName
          occupation
        }
      }
    `;

    const fullAddress = [
      guardian.address?.unitNumber && guardian.address?.streetName
        ? `${guardian.address.unitNumber} ${guardian.address.streetName}`
        : guardian.address?.streetName || '',
      guardian.address?.complex || '',
      guardian.address?.suburb || '',
      guardian.address?.city || '',
      guardian.address?.region || '',
    ]
      .filter(Boolean)
      .join(', ');

    const personMutation = gql`
      mutation UpdatePerson {
        updatePerson(
          input: {
            personaId: "${guardian.id}",
            nickName: "${guardian.username}",
            titleInput: {id: "${guardian.title.id}"},
            address: {
              id: "${guardian.address?.id || ''}",
              complexErf: "${guardian.address?.complex || ''}",
              postalCode: "${guardian.address?.areaCode || ''}",
              region: "${guardian.address?.region || ''}",
              streetName: "${guardian.address?.unitNumber ? guardian.address.unitNumber + ' ' : ''}${guardian.address?.streetName || ''}",
              suburb: "${guardian.address?.suburb || ''}",
              town: "${guardian.address?.city || ''}",
              addressType: {id: "${guardian.address?.type?.id || ''}"},
              address: "${fullAddress}"
            }
          }
        ) {
          nickName
          title {
            id
          }
          addresses {
            address
            complexErf
            id
            postalCode
            region
            streetName
            suburb
            town
            addressType {
              id
            }
          }
          emailAddresses {
            id
            address
            emailAddressType {
              id
            }
          }
          phoneNumbers {
            id
            number
            phoneNumberType {
              id
            }
            nationalNumber
            mobileNumber
            countryCode
          }
        }
      }
    `;

    return this.apollo.mutate({ mutation: guardianMutation }).pipe(
      map((guardianResult) => {
        return this.apollo.mutate({ mutation: personMutation }).pipe(
          map((personResult) => {
            const combinedResult = {
              guardian: guardianResult.data,
              person: personResult.data,
            };
            return combinedResult;
          }),
        );
      }),
      // Flatten the nested Observable
      mergeMap((result) => result),
    );
  }

  updateUserInfoPhone(guardian: IGuardian): Observable<any> {
    const guardianMutation = gql`
      mutation UpdateGuardian {
        updateGuardian(
          input: {id: "${guardian.id}", parentalStatus: {id: "${guardian.parentalStatus.id}"}, relationship: {id: "${guardian.learnerRelationship?.id}"}, maritalStatus: "${guardian.maritalStatus}"}
        ) {
          parentalStatus {
            id
          }
          relationship {
            id
          }
          maritalStatus
        }
      }
    `;

    const fullAddress = [
      guardian.address?.unitNumber && guardian.address?.streetName
        ? `${guardian.address.unitNumber} ${guardian.address.streetName}`
        : guardian.address?.streetName || '',
      guardian.address?.complex || '',
      guardian.address?.suburb || '',
      guardian.address?.city || '',
      guardian.address?.region || '',
    ]
      .filter(Boolean)
      .join(', ');

    const personMutation = gql`
      mutation UpdatePerson {
        updatePerson(
          input: {
            personaId: "${guardian.id}",
            nickName: "${guardian.username}",
            titleInput: {id: "${guardian.title.id}"},
            address: {
              id: "${guardian.address?.id || ''}",
              complexErf: "${guardian.address?.complex || ''}",
              postalCode: "${guardian.address?.areaCode || ''}",
              region: "${guardian.address?.region || ''}",
              streetName: "${guardian.address?.unitNumber ? guardian.address.unitNumber + ' ' : ''}${guardian.address?.streetName || ''}",
              suburb: "${guardian.address?.suburb || ''}",
              town: "${guardian.address?.city || ''}",
              addressType: {id: "${guardian.address?.type?.id || ''}"},
              address: "${fullAddress}"
            },
            phoneNumber: {
              id: "${guardian.numberUpdate?.id}",
              number: "${this.convertNumber(guardian.numberUpdate?.number as string, guardian.numberUpdate?.cellCode)}",
              nationalNumber: "${guardian.numberUpdate?.number}",
              mobileNumber: true,
              phoneNumberType: { id: "${guardian.numberUpdate?.type}" },
              countryCode: "${this.convertCellCode(guardian.numberUpdate?.cellCode as string)}"
            }
          }
        ) {
          nickName
          title {
            id
          }
          addresses {
            address
            complexErf
            id
            postalCode
            region
            streetName
            suburb
            town
            addressType {
              id
            }
          }
          emailAddresses {
            id
            address
            emailAddressType {
              id
            }
          }
          phoneNumbers {
            id
            number
            phoneNumberType {
              id
            }
            nationalNumber
            mobileNumber
            countryCode
          }
        }
      }
    `;

    return this.apollo.mutate({ mutation: guardianMutation }).pipe(
      map((guardianResult) => {
        return this.apollo.mutate({ mutation: personMutation }).pipe(
          map((personResult) => {
            const combinedResult = {
              guardian: guardianResult.data,
              person: personResult.data,
            };
            return combinedResult;
          }),
        );
      }),
      // Flatten the nested Observable
      mergeMap((result) => result),
    );
  }

  updateUserInfoEmail(guardian: IGuardian): Observable<any> {
    const guardianMutation = gql`
      mutation UpdateGuardian {
        updateGuardian(
          input: {id: "${guardian.id}", parentalStatus: {id: "${guardian.parentalStatus.id}"}, relationship: {id: "${guardian.learnerRelationship?.id}"}, maritalStatus: "${guardian.maritalStatus}"}
        ) {
          parentalStatus {
            id
          }
          relationship {
            id
          }
          maritalStatus
        }
      }
    `;

    const fullAddress = [
      guardian.address?.unitNumber && guardian.address?.streetName
        ? `${guardian.address.unitNumber} ${guardian.address.streetName}`
        : guardian.address?.streetName || '',
      guardian.address?.complex || '',
      guardian.address?.suburb || '',
      guardian.address?.city || '',
      guardian.address?.region || '',
    ]
      .filter(Boolean)
      .join(', ');

    const personMutation = gql`
      mutation UpdatePerson {
        updatePerson(
          input: {
            personaId: "${guardian.id}",
            nickName: "${guardian.username}",
            titleInput: {id: "${guardian.title.id}"},
            address: {
              id: "${guardian.address?.id || ''}",
              complexErf: "${guardian.address?.complex || ''}",
              postalCode: "${guardian.address?.areaCode || ''}",
              region: "${guardian.address?.region || ''}",
              streetName: "${guardian.address?.unitNumber ? guardian.address.unitNumber + ' ' : ''}${guardian.address?.streetName || ''}",
              suburb: "${guardian.address?.suburb || ''}",
              town: "${guardian.address?.city || ''}",
              addressType: {id: "${guardian.address?.type?.id || ''}"},
              address: "${fullAddress}"
            },
            emailAddress: {id: "${guardian.primaryEmail?.id}",
            address: "${guardian.primaryEmail?.address}",emailAddressType: {id: "${guardian.primaryEmail?.emailAddressType?.id}"}}
          }
        ) {
          nickName
          title {
            id
          }
          addresses {
            address
            complexErf
            id
            postalCode
            region
            streetName
            suburb
            town
            addressType {
              id
            }
          }
          emailAddresses {
            id
            address
            emailAddressType {
              id
            }
          }
          phoneNumbers {
            id
            number
            phoneNumberType {
              id
            }
            nationalNumber
            mobileNumber
            countryCode
          }
        }
      }
    `;

    return this.apollo.mutate({ mutation: guardianMutation }).pipe(
      map((guardianResult) => {
        return this.apollo.mutate({ mutation: personMutation }).pipe(
          map((personResult) => {
            const combinedResult = {
              guardian: guardianResult.data,
              person: personResult.data,
            };
            return combinedResult;
          }),
        );
      }),
      // Flatten the nested Observable
      mergeMap((result) => result),
    );
  }

  addPersonEmailAddress(personaId: string, address: IEmail): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation AddPersonEmailAddress($personaId: ID!, $address: String!, $emailTypeId: ID!) {
            addPersonEmailAddress(
              input: {
                personaId: $personaId
                emailAddress: { address: $address, emailAddressType: { id: $emailTypeId } }
              }
            ) {
              emailAddresses {
                address
                id
                emailAddressType {
                  id
                  description
                }
              }
              id
            }
          }
        `,
        variables: {
          personaId,
          address: address.address,
          emailTypeId: address.emailAddressType?.id,
        },
      })
      .pipe(
        timeout(3000),
        tap({
          error: (error) => console.error('Error adding email address:', error),
        }),
      );
  }

  addPersonPhoneNumber(guardian: IGuardian): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation AddPersonPhoneNumber(
            $personId: ID!
            $number: String!
            $nationalNumber: String!
            $countryCode: String!
            $phoneNumberTypeId: ID!
            $mobileNumber: Boolean!
          ) {
            addPersonPhoneNumber(
              input: {
                personaId: $personId
                phoneNumber: {
                  number: $number
                  nationalNumber: $nationalNumber
                  mobileNumber: $mobileNumber
                  countryCode: $countryCode
                  phoneNumberType: { id: $phoneNumberTypeId }
                }
              }
            ) {
              id
              phoneNumbers {
                countryCode
                id
                mobileNumber
                nationalNumber
                number
                phoneNumberType {
                  id
                  description
                }
              }
            }
          }
        `,
        variables: {
          personId: guardian.id,
          phoneNumberTypeId: guardian.numberUpdate?.type,
          number: this.convertNumber(guardian.numberUpdate?.number as string, guardian.numberUpdate?.cellCode),
          nationalNumber: guardian.numberUpdate?.number,
          countryCode: this.convertCellCode(guardian.numberUpdate?.cellCode as string),
          mobileNumber: true,
        },
      })
      .pipe(
        timeout(3000),
        tap({
          error: (error) => console.error('Phone number add error:', error),
        }),
      );
  }

  deletePhoneNumberById(phoneNumberId: string): Observable<any> {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation DeletePhoneNumberById($phoneNumberId: String!) {
            deletePhoneNumberById(phoneNumberId: $phoneNumberId) {
              deleted
            }
          }
        `,
        variables: {
          phoneNumberId,
        },
      })
      .pipe(
        timeout(3000),
        tap({
          error: (error) => console.error('Error deleting phone number:', error),
        }),
        catchError((error) => {
          console.error('Failed to delete phone number:', error);
          return of({ deleted: false });
        }),
      );
  }

  updateLearnerAccess(idNumber: string, learnerAccess: ILearnerAccess[]): Observable<any> {
    const upgradeEndpointUrl = `${environment.apiBaseUrl}/myhub-rbac/api/user/update/adminOnSelectedStudents`;

    // Group learners by userType to handle different access levels
    const adminLearners = learnerAccess.filter((access) => access.userType === 'Admin');
    const coparentLearners = learnerAccess.filter(
      (access) => access.userType === 'Co-Parent' || access.userType === 'Coparent',
    );
    const revokeAccessLearners = learnerAccess.filter((access) => access.userType === null);

    const requests: Observable<any>[] = [];

    // Filter learners based on selected account codes
    const filteredAdminLearners = adminLearners.filter((learner) =>
      this.selectedAccountCodes()?.includes(learner.accountCode as string),
    );

    const filteredCoparentLearners = coparentLearners.filter((learner) =>
      this.selectedAccountCodes()?.includes(learner.accountCode as string),
    );

    const filteredRevokeLearners = revokeAccessLearners.filter((learner) =>
      this.selectedAccountCodes()?.includes(learner.accountCode as string),
    );

    // Handle Admin access
    if (filteredAdminLearners.length > 0) {
      const payload = {
        guardianIdNumber: idNumber,
        roleName: 'Admin',
        revokeAccess: false,
        accessChangeGuardianId: this.loggedInGuardianId(),
        students: filteredAdminLearners.map((learner) => ({
          id: learner.learnerId,
          accountCode: learner.accountCode || '',
          schoolId: learner.schoolId || '',
          companyCode: learner.companyCode || '',
        })),
      };

      requests.push(
        this.http
          .post(upgradeEndpointUrl, payload, {
            responseType: 'text', // ✅ Handle text response
          })
          .pipe(
            timeout(5000),
            map((response) => ({ success: true, message: response })),
            catchError((error) => {
              console.error('Error updating admin access:', error);
              return of({ success: false, error });
            }),
          ),
      );
    }

    // Handle Co-Parent access
    if (filteredCoparentLearners.length > 0) {
      const payload = {
        guardianIdNumber: idNumber,
        roleName: 'Co-Parent',
        revokeAccess: false,
        accessChangeGuardianId: this.loggedInGuardianId(),
        students: filteredCoparentLearners.map((learner) => ({
          id: learner.learnerId,
          accountCode: learner.accountCode || '',
          schoolId: learner.schoolId || '',
          companyCode: learner.companyCode || '',
        })),
      };

      requests.push(
        this.http
          .post(upgradeEndpointUrl, payload, {
            responseType: 'text', // ✅ Handle text response
          })
          .pipe(
            timeout(5000),
            map((response) => ({ success: true, message: response })),
            catchError((error) => {
              console.error('Error updating Co-Parent access:', error);
              return of({ success: false, error });
            }),
          ),
      );
    }

    // Handle revoking access
    if (filteredRevokeLearners.length > 0) {
      const payload = {
        guardianIdNumber: idNumber,
        roleName: 'Co-Parent',
        revokeAccess: true,
        accessChangeGuardianId: this.loggedInGuardianId(),
        students: filteredRevokeLearners.map((learner) => ({
          id: learner.learnerId,
          accountCode: learner.accountCode || '',
          schoolId: learner.schoolId || '',
          companyCode: learner.companyCode || '',
        })),
      };

      requests.push(
        this.http
          .post(upgradeEndpointUrl, payload, {
            responseType: 'text', // ✅ Handle text response
          })
          .pipe(
            timeout(5000),
            map((response) => ({ success: true, message: response })),
            catchError((error) => {
              console.error('Error revoking learner access:', error);
              return of({ success: false, error });
            }),
          ),
      );
    }

    // Execute all requests
    if (requests.length > 0) {
      return forkJoin(requests).pipe(
        map((results) => ({
          success: true,
          results,
          message: 'All access updates completed successfully',
        })),
        catchError((error) => {
          console.error('Error in updateLearnerAccess:', error);
          return of({ success: false, error });
        }),
      );
    }

    // Clear selected account codes after processing
    this.store.dispatch(AccessActions.setSelectedAccountCodes({ selectedAccountCodes: [] }));

    // If no requests needed
    return of({ success: true, message: 'No changes needed' });
  }

  convertNumber(number: string, countryCode: string | undefined): string {
    let phoneNumber = '';

    if (countryCode && number.startsWith('0')) {
      phoneNumber = countryCode + number.substring(1);
    } else {
      phoneNumber = countryCode + number;
    }

    return phoneNumber;
  }

  convertCellCode(dialCode: string): string {
    if (!dialCode) return '';
    const countries = this.lookupService.countries();
    const match = countries.find((c) => c.dialCode === dialCode);
    return match?.code ?? '';
  }

  sendInviteToGuardian(guardian: IGuardian): Observable<any> {
    // Get the company code from the guardian's account links, fallback to "CPG" if not available
    const companyCode =
      guardian.accountLinks && guardian.accountLinks.length > 0 ? guardian.accountLinks[0].companyCode : '';

    const payload = {
      companyCode: companyCode,
      accountLinkId: guardian.accountLinkId,
      personId: guardian.personId,
    };

    return this.http.post(`${environment.apiBaseUrl}/myhub-api/invitation/guardian-invite`, payload).pipe(
      timeout(5000),
      tap({
        error: (error) => console.error('Error sending guardian invite:', error),
      }),
      catchError((error) => {
        console.error('Failed to send guardian invite:', error);
        return of({ success: false, error: error.message });
      }),
    );
  }

  checkEmailExists(email: string): Observable<boolean> {
    if (!email) return of(false);

    const query = gql`
      query EmailExists {
        emailExists(emailAddress: "${email}")
      }
    `;

    return this.apollo.query<{ emailExists: boolean }>({ query }).pipe(
      map((result) => result.data?.emailExists ?? false),
      catchError(() => of(false)),
    );
  }

  private mapInviteStatusToGuardianStatus(myhubInviteStatusCode: 'ACT' | 'IAC' | 'PED'): string {
    switch (myhubInviteStatusCode) {
      case 'ACT':
        return 'ACT'; // Active
      case 'IAC':
        return 'IAC'; // Inactive
      case 'PED':
        return 'PED'; // Pending
      default:
        return 'ACT'; // Default to pending
    }
  }
}
